import {NavigationProps} from "../constants/Props";
import {observer} from "mobx-react";
import Screen from "../components/Screen";
import Box, {Direction} from "../components/Box";
import {isMobile} from "react-device-detect";
import Title from "../components/Title";
import {toI18N} from "../utils/I18n";
import classNames from "classnames";
import textStyles from "../css/Text.module.css";
import styles from "../css/Text.module.css";
import Switch from "react-switch";

import React, {useCallback, useEffect, useMemo} from "react";
import {IGroupStore} from "../stores/GroupStore";
import MultiGroupSelector from "../components/MultiGroupSelector";
import {IGroup} from "../stores/models";
import {usePrevious} from "../utils/HooksHelper";
import * as Analytics from "../utils/Analytics";

interface Props extends NavigationProps {
    store: IGroupStore
    groups: IGroup[]
}

function GroupSettingScreen(props: Props) {
    const {
        onBack,
        onNext,
        store,
        groups,
        isActive,
    } = props;

    const prevIsActive = usePrevious(isActive);
    useEffect(() => {
        if (!prevIsActive && isActive) {
            Analytics.logEvent(Analytics.events.land_on_group_settings_page);
        }
    });


    const {setFeatureGroupIds} = store;

    // Memo

    const options = useMemo(() => {
        return groups.map(({id, name, selectId}, index) => {
            return {id, selectId, name, value: `${index}`, label: `${name} ${selectId}`};
        });
    }, [groups]);

    const value: any = store.featureGroupIds.map((groupId) => {
        return options.find(({id}) => id === groupId);
    });

    const selectFeatureGroup = useCallback(
        (indexes) => {
            if (!indexes) {
                setFeatureGroupIds([]);
                return;
            }
            setFeatureGroupIds(indexes.map((v: number) => groups[v].id));

        },
        [groups, setFeatureGroupIds],
    );

    return (
        <Screen
            title={"group_setting_screen_header_title"}
            disabledKeyDown
            subtitle={"group_setting_screen_header_subtitle"}
            onBack={onBack}
            onNext={onNext}
        >
            <Box shrink center topMargin={isMobile ? 45 : 100}>
                <Title lg shrink title={toI18N('group_setting_screen_title')}/>
            </Box>
            <Box sideMargin shrink center bottomMargin={20}>
                <SwitchWithLabel checked={!store.isOpen} onChange={(value) => store.setIsOpen(!value)}
                    title={toI18N("hl_privacy_setting_open_title")}
                    label={store.isOpen ?
                        toI18N("hl_privacy_setting_open_label") :
                        toI18N("hl_privacy_setting_close_label")}/>
            </Box>
            <Box sideMargin shrink center bottomMargin={20}>
                <SwitchWithLabel checked={store.isGroupHistoryOn} onChange={store.setIsGroupHistoryOn}
                    title={toI18N("group_setting_screen_group_history_title")}
                    label={store.isGroupHistoryOn ?
                        toI18N("group_setting_screen_group_history_on_label") :
                        toI18N("group_setting_screen_group_history_off_label")}/>
            </Box>
            {
                store.isGroupHistoryOn ?  <Box sideMargin shrink center bottomMargin={20}>
                    <SwitchWithLabel checked={store.shareToPublic} onChange={store.setShareToPublic}
                                     title={toI18N("group_setting_screen_group_share_to_public_title")}
                                     label={store.isGroupHistoryOn ?
                                         toI18N("group_setting_screen_group_share_to_public_on_label") :
                                         toI18N("group_setting_screen_group_share_to_public_off_label")}/>
                </Box> : null
            }
            <Box sideMargin shrink center bottomMargin={20}>
                <SwitchWithLabel checked={!store.allowCrossExposure} onChange={(value) => store.setAllowCrossExposure(!value)}
                                 title={toI18N("group_setting_screen_cross_exposure_title")}
                                 label={!store.allowCrossExposure ?
                                     toI18N("group_setting_screen_cross_exposure_on_label") :
                                     toI18N("group_setting_screen_cross_exposure_off_label")}/>
            </Box>
            <Box sideMargin shrink center bottomMargin={25}>
                <SwitchWithLabel checked={store.isMembersCanPostOn} onChange={store.setIsMembersCanPostOn}
                    title={toI18N("group_setting_screen_member_post_title")}
                    label={store.isMembersCanPostOn ?
                        toI18N("group_setting_screen_member_post_on_label") :
                        toI18N("group_setting_screen_member_post_off_label")}/>
            </Box>
            <Box shrink center sideMargin direction={Direction.Column}>
                <Box direction={Direction.Clear}>
                    <MultiGroupSelector
                        id={'select-group-multi-group-selector'}
                        placeholder={toI18N("group_setting_screen_feature_group_placeholder")}
                        options={options}
                        label={toI18N("group_setting_screen_feature_group_label")}
                        hint={toI18N("group_setting_screen_feature_group_hint")}
                        onSelectGroup={selectFeatureGroup}
                        value={value}
                    />
                </Box>
            </Box>
            <Box grow/>
        </Screen>
    )
}

interface SwitchWithLabelProps {
    title: string,
    label: string,
    checked: boolean
    onChange: (
        checked: boolean,
        event: React.SyntheticEvent<MouseEvent | KeyboardEvent> | MouseEvent,
        id: string
    ) => void
}

export function SwitchWithLabel(props: SwitchWithLabelProps) {
    const {checked, onChange, title, label} = props
    return (
        <>
            <Box
                spaceBetween
                grow
            >
                <Title shrink title={title}/>
                <Switch width={70} checked={checked} onChange={onChange} onColor={'#00aab1'}
                    uncheckedIcon={<h3 style={{color: 'white', paddingTop: 1}}
                        className={classNames({[styles.h3x]: true,})}>{toI18N('group_setting_screen_title_off_option')}</h3>}
                    checkedIcon={<h3 style={{color: 'white', paddingTop: 1}}
                        className={classNames({[styles.h3x]: true,})}>{toI18N('group_setting_screen_title_on_option')}</h3>}/>
            </Box>
            <Box
                grow
            >
                <label
                    className={textStyles.hint} style={{height: 35}}>{label}</label>
            </Box>
        </>
    )
}

export default observer(GroupSettingScreen);
